import type { PixelEvent } from '@/events';
import type { PixelModule } from '@/runtime/types';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import { DispatcherSubscriber } from './subscriber/DispatcherSubscriber';
import type { TransportDebugState } from './transport.engine';
import { TransportEngine } from './transport.engine';
import type { TestSender } from './sender/TestSender';

export class TransportModule implements PixelModule {
  readonly name = 'transport' as const;

  private engine: TransportEngine | null = null;

  private subscriber: DispatcherSubscriber | null = null;

  private active = false;

  async init(context: PixelRuntimeContext): Promise<void> {
    if (this.active) {
      context.logger.debug('[Pixel] Transport module already initialized');
      return;
    }

    if (!context.config.transport.enabled) {
      context.logger.debug('[Pixel] Transport module disabled');
      return;
    }

    context.logger.debug('[Pixel] Transport module initialized');

    this.engine = new TransportEngine(context);
    await this.engine.init();

    this.subscriber = new DispatcherSubscriber(
      context.services.dispatcher,
      event => this.engine?.handleEvent(event)
    );
    this.subscriber.subscribe();

    this.active = true;
  }

  async destroy(): Promise<void> {
    if (!this.active) return;

    this.subscriber?.unsubscribeAll();
    this.subscriber = null;

    this.engine?.destroy();
    this.engine = null;

    this.active = false;
  }

  getDebugState(): TransportDebugState | null {
    return this.engine?.getDebugState() ?? null;
  }

  isEngineActive(): boolean {
    return this.engine?.isActive() ?? false;
  }

  manualFlush(): void {
    this.engine?.manualFlush();
  }

  simulateOffline(offline: boolean): void {
    this.engine?.simulateOffline(offline);
  }

  configureTestSender(options: Parameters<TestSender['configure']>[0]): void {
    this.engine?.configureTestSender(options);
  }

  simulateReload(): Promise<void> {
    return this.engine?.simulateReload() ?? Promise.resolve();
  }

  simulateRestart(): Promise<void> {
    return this.engine?.simulateRestart() ?? Promise.resolve();
  }

  forceCircuitOpen(): void {
    this.engine?.forceCircuitOpen();
  }

  forceCircuitClose(): void {
    this.engine?.forceCircuitClose();
  }

  manualReplay(): Promise<number> {
    return this.engine?.manualReplay() ?? Promise.resolve(0);
  }

  replayDeadLetter(): PixelEvent[] {
    return this.engine?.replayDeadLetter() ?? [];
  }

  clearDeadLetter(): void {
    this.engine?.clearDeadLetter();
  }

  exportQueue(): string {
    return this.engine?.exportQueue() ?? '[]';
  }

  importQueue(raw: string): number {
    return this.engine?.importQueue(raw) ?? 0;
  }
}
